import { CollectionsQuery } from "services/graphql/generated";
import { nameToSlug } from "@pepdirect/v3/helpers/string";

/**
 * NavItem structure matching NavHeader's expected format
 */
export interface NavItem {
  title: string;
  link?: string;
  links?: {
    display: string;
    url: string;
    id?: string;
  }[];
  isOpenNewTab?: boolean;
  id?: string;
}

/**
 * Hardcoded story_slug navigation items for PantryShop
 * Used as fallback when Algolia collections are not available
 * First item is a dropdown with "Shop All", rest are direct links
 */
const STORY_SLUG_NAV_ITEMS: NavItem[] = [
  {
    title: "Pantry Kits",
    links: [
      { display: "Shop All Bundles", url: "/kit/all-bundles" },
      { display: "Protein", url: "/kit/_02f37ece" },
      { display: "Rise & Shine", url: "/kit/_97a5bc1e" },
      { display: "Family Favorites", url: "/kit/_93ea5b42" },
      { display: "Snacking", url: "/kit/_a00c2fa0" },
      { display: "Everyday Pantry", url: "/kit/pantry" },
    ],
  },
  {
    title: "Beverages",
    link: "/kit/beverages",
  },
];

/**
 * Static navigation items that always appear at the end
 */
export const STATIC_END_NAV_ITEMS: NavItem[] = [
  {
    title: "Our Mission",
    link: "/our-mission",
  },
  {
    title: "Contact",
    link: "https://contact.pepsico.com/pantryshop",
    isOpenNewTab: true,
  },
];

/**
 * Builds NavItem array from collections data
 * Creates a dropdown for all collections under "Pantry Kits"
 * Falls back to hardcoded story_slug items if no collections provided
 * @param collections - Collections from GraphQL query (can be undefined)
 * @returns NavItem array for dynamic navigation (without static end items)
 */
export function buildNavItemsFromCollections(
  collections: CollectionsQuery["collections"] | undefined
): NavItem[] {
  // Fallback to hardcoded items if no collections
  if (!collections || collections.length === 0) {
    return STORY_SLUG_NAV_ITEMS;
  }

  // Create dropdown links for all collections under first item
  const dropdownLinks = collections.map((collection) => ({
    display: collection.name,
    url: `/kit/${nameToSlug(collection.name)}`,
  }));

  // First item is a dropdown with all collections
  const dropdownItem: NavItem = {
    title: "Pantry Kits",
    links: [{ display: "Shop All", url: "/kit/all-bundles" }, ...dropdownLinks],
  };

  return [dropdownItem];
}
